import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { type FC, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { pagesApiSlice } from '../../../../store/api/pages-api/pages-api';
import { v2Theme } from './v2-theme';

export const PageSuccessV2: FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const location = useLocation();
  const { data } = pagesApiSlice.useGetAggPagesV2ByIdQuery(Number(pageId), {
    skip: !pageId,
  });

  const [copied, setCopied] = useState(false);

  const page = data?.page;
  const pageUrl: string = location.state?.pageUrl || page?.link || '';

  const handleCopy = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ThemeProvider theme={v2Theme}>
    <Box sx={{
      alignItems: 'center',
      bgcolor: 'grey.50',
      display: 'flex',
      justifyContent: 'center',
      minHeight: '80vh',
      px: 2,
    }}>
      <Box sx={{ maxWidth: 500, width: '100%' }}>

        {/* Success header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{
            alignItems: 'center',
            bgcolor: 'success.main',
            borderRadius: '50%',
            display: 'inline-flex',
            justifyContent: 'center',
            mb: 2.5,
            p: 2,
          }}>
            <CheckCircleIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography fontWeight={700} gutterBottom variant="h5">
            Page saved!
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Your page has been saved and is ready to go.
          </Typography>
        </Box>

        {/* URL card */}
        {pageUrl && (
          <Box sx={{
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 3,
            overflow: 'hidden',
          }}>
            <Box sx={{
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}>
              <Typography color="text.secondary" sx={{ letterSpacing: 0.5 }} variant="caption">
                PAGE URL
              </Typography>
              <Chip color="success" label="Live" size="small" variant="outlined" />
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, px: 2, py: 1.5 }}>
              <Typography
                sx={{
                  color: 'text.primary',
                  flex: 1,
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {pageUrl}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                <IconButton onClick={handleCopy} size="small">
                  <ContentCopyIcon fontSize="small" sx={{ color: copied ? 'success.main' : 'text.secondary' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton component="a" href={pageUrl} rel="noopener noreferrer" size="small" target="_blank">
                  <OpenInNewOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Stack divider={<Divider />}>
            <Button
              component={Link}
              fullWidth
              replace
              size="large"
              startIcon={<EditOutlinedIcon />}
              sx={{ borderRadius: 0, justifyContent: 'flex-start', px: 2.5, py: 1.75, textTransform: 'none' }}
              to={`/pages/v2/${pageId}`}
              variant="text"
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography fontWeight={600} variant="body2">Continue editing</Typography>
                <Typography color="text.secondary" variant="caption">Make more changes to this page</Typography>
              </Box>
            </Button>

            {pageUrl && (
              <Button
                component="a"
                fullWidth
                href={pageUrl}
                rel="noopener noreferrer"
                size="large"
                startIcon={<OpenInNewOutlinedIcon />}
                sx={{ borderRadius: 0, justifyContent: 'flex-start', px: 2.5, py: 1.75, textTransform: 'none' }}
                target="_blank"
                variant="text"
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography fontWeight={600} variant="body2">View page</Typography>
                  <Typography color="text.secondary" variant="caption">Open the published page in a new tab</Typography>
                </Box>
              </Button>
            )}

            <Button
              component={Link}
              fullWidth
              replace
              size="large"
              startIcon={<FormatListBulletedIcon />}
              sx={{
                borderRadius: 0,
                color: 'text.secondary',
                justifyContent: 'flex-start',
                px: 2.5,
                py: 1.75,
                textTransform: 'none',
              }}
              to="/pages"
              variant="text"
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography fontWeight={600} variant="body2">Back to pages</Typography>
                <Typography color="text.secondary" variant="caption">Return to the pages list</Typography>
              </Box>
            </Button>
          </Stack>
        </Box>

      </Box>
    </Box>
    </ThemeProvider>
  );
};
